import { Fragment } from "react";
import { Link } from "react-router";

import {
  Tab,
  Tabs,
  TabsList,
  TabsSeparator,
  Tooltip,
} from "@/shared/foundation/primitives";

import {
  TRIGGER_DETAIL_TAB_GROUPS,
  TRIGGER_DETAIL_TAB_LABELS,
  triggerDetailPath,
  type TriggerDetailTab,
  type TriggerDetailTabName,
} from "./triggerDetailTabs";

interface TriggerDetailTabBarProps {
  triggerName: string;
  tabs: readonly TriggerDetailTab[];
  activeTab: TriggerDetailTabName;
}

/**
 * Tab strip for the trigger detail pages.
 *
 * Every tab is a `Link` to its own URL and the active one is passed in from
 * the route, so the strip holds no state and a tab is shareable and
 * bookmarkable. The strip is two groups with a rule between them: the
 * trigger and its history, then what its runs produced elsewhere. A tab
 * with nothing to show stays in place, greyed, with the reason on hover, and
 * after the group's live tabs so the strip never alternates.
 */
export function TriggerDetailTabBar({
  triggerName,
  tabs,
  activeTab,
}: TriggerDetailTabBarProps) {
  const byName = new Map(tabs.map((tab) => [tab.name, tab]));
  const isGreyed = (name: TriggerDetailTabName) =>
    byName.get(name)?.unavailableReason !== null;

  return (
    <Tabs value={activeTab}>
      <TabsList aria-label="trigger detail tabs">
        {TRIGGER_DETAIL_TAB_GROUPS.map((group, groupIndex) => (
          <Fragment key={group.join()}>
            {groupIndex > 0 && <TabsSeparator />}
            {[...group]
              .sort((a, b) => Number(isGreyed(a)) - Number(isGreyed(b)))
              .map((name) => {
                const reason = byName.get(name)?.unavailableReason ?? null;
                return reason === null ? (
                  <Tab
                    key={name}
                    value={name}
                    nativeButton={false}
                    render={
                      <Link to={triggerDetailPath(triggerName, name)} replace />
                    }
                  >
                    {TRIGGER_DETAIL_TAB_LABELS[name]}
                  </Tab>
                ) : (
                  <Tooltip key={name}>
                    <Tooltip.Trigger
                      render={
                        <Tab value={name} disabled aria-description={reason}>
                          {TRIGGER_DETAIL_TAB_LABELS[name]}
                        </Tab>
                      }
                    />
                    <Tooltip.Content side="bottom">{reason}</Tooltip.Content>
                  </Tooltip>
                );
              })}
          </Fragment>
        ))}
      </TabsList>
    </Tabs>
  );
}
